import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';

const ModernDrugSearch = () => {
  // Track if search started event was fired for current session
  const searchStartedRef = useRef(false);
  
  // Initialize query from URL params (for sticky search on drug results page)
  const getInitialQuery = () => {
    if (typeof window === 'undefined') return '';
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('q') || '';
  };
  
  const [query, setQuery] = useState(getInitialQuery());
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState(null);
  const [initialQuery] = useState(getInitialQuery());

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceTimer = useRef(null);
  const containerRef = useRef(null);

  // Fetch suggestions from API
  const fetchSuggestions = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.trim().length === 0) {
      setSuggestions([]);
      setIsOpen(false);
      setHasSearched(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`/drugs?q=${encodeURIComponent(searchQuery.trim())}`);

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      const results = data?.data || [];

      setSuggestions(results);
      setIsOpen(true); // Always open to show results or "no results" state
      setHasSearched(true);
      setHighlightedIndex(-1);
    } catch (err) {
      console.error('Error fetching drug suggestions:', err);
      setSuggestions([]);
      setError('Unable to search. Please try again.');
      setIsOpen(true);
      setHasSearched(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      fetchSuggestions(query);
    }, 300);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [query, fetchSuggestions]);

  // Update dropdown position
  const updateDropdownPosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, []);

  // Update search button state based on query changes
  const updateSearchButtonState = useCallback(() => {
    const form = inputRef.current?.closest('form');
    if (!form) return;

    const submitBtn = form.querySelector('button[type="submit"]');
    if (!submitBtn) return;

    const hasQueryChanged = query.trim() !== initialQuery.trim();

    if (hasQueryChanged && query.trim().length > 0) {
      // Query has changed - enable button fully
      submitBtn.disabled = false;
      submitBtn.style.opacity = '1';
      submitBtn.style.cursor = 'pointer';
    } else if (!query.trim()) {
      // Empty query - disable button
      submitBtn.disabled = true;
      submitBtn.style.opacity = '0.5';
      submitBtn.style.cursor = 'not-allowed';
    } else {
      // Query exists but hasn't changed - dim button
      submitBtn.disabled = false;
      submitBtn.style.opacity = '0.6';
      submitBtn.style.cursor = 'pointer';
    }
  }, [query, initialQuery]);

  // Update button state when query changes
  useEffect(() => {
    updateSearchButtonState();
  }, [query, updateSearchButtonState]);

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setQuery(newValue);
    updateDropdownPosition();

    // Track search started (only once per session)
    if (newValue.length > 0 && !searchStartedRef.current) {
      searchStartedRef.current = true;
      const source = window.location.pathname === '/' ? 'home' : 'results';
      if (typeof Analytics !== 'undefined') {
        Analytics.trackSearchStarted(source);
      }
    }
  };

  // Handle suggestion selection
  const selectSuggestion = (suggestion, index = -1) => {
    setQuery(suggestion.name);
    setSuggestions([]);
    setIsOpen(false);
    setHighlightedIndex(-1);

    // Track autocomplete selection
    const source = window.location.pathname === '/' ? 'home' : 'results';
    if (typeof Analytics !== 'undefined') {
      Analytics.trackAutocompleteSelected({
        drugName: suggestion.name,
        index: index >= 0 ? index : suggestions.indexOf(suggestion),
        query: query,
        source: source
      });
    }

    // Auto-submit the form when a suggestion is selected
    const form = inputRef.current?.closest('form');
    if (form) {
      // Small delay to ensure the input value is updated
      setTimeout(() => {
        form.submit();
      }, 100);
    }
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen || suggestions.length === 0) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;

      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;

      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          selectSuggestion(suggestions[highlightedIndex], highlightedIndex);
        }
        break;

      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;

      default:
        break;
    }
  };

  // Handle click outside and window resize/scroll
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    const handleWindowResize = () => {
      updateDropdownPosition();
    };

    const handleScroll = () => {
      updateDropdownPosition();
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleWindowResize);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleWindowResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [updateDropdownPosition]);

  // Render suggestion item
  const renderSuggestion = (suggestion, index) => {
    const isHighlighted = index === highlightedIndex;

    let genericText = '';
    if (suggestion.is_generic) {
      genericText = '(Generic)';
    } else if (suggestion.generic_name) {
      genericText = `(${suggestion.generic_name})`;
    }

    const classification = suggestion.classification === 'non-prescription'
      ? 'Non-Prescription'
      : '';

    return (
      <div
        key={`${suggestion.name}-${index}`}
        onClick={() => selectSuggestion(suggestion, index)}
        onMouseEnter={() => setHighlightedIndex(index)}
        style={{
          padding: '12px 20px',
          cursor: 'pointer',
          backgroundColor: isHighlighted ? 'rgba(124, 58, 237, 0.08)' : 'transparent',
          borderBottom: index < suggestions.length - 1 ? '1px solid rgba(226, 232, 240, 0.6)' : 'none',
          transition: 'background-color 0.15s ease'
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <div>
            <span style={{
              fontWeight: '500',
              fontSize: '15px',
              color: '#1e293b'
            }}>
              {suggestion.name}
            </span>
            {genericText && (
              <span style={{
                marginLeft: '8px',
                fontSize: '14px',
                color: '#64748b',
                fontWeight: '400'
              }}>
                {genericText}
              </span>
            )}
          </div>
          {classification && (
            <span style={{
              fontSize: '12px',
              color: '#94a3b8'
            }}>
              {classification}
            </span>
          )}
        </div>
      </div>
    );
  };

  // Render "No results" state
  const renderNoResults = () => (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <div style={{
        width: '48px',
        height: '48px',
        margin: '0 auto 12px',
        borderRadius: '50%',
        backgroundColor: '#f1f5f9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"></circle>
          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
      </div>
      <p style={{ fontWeight: '500', color: '#1e293b', marginBottom: '4px', fontSize: '15px' }}>
        No medications found
      </p>
      <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '12px' }}>
        Try checking the spelling or search for the generic name
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center' }}>
        {['Lipitor', 'Metformin', 'Lisinopril'].map(drug => (
          <button
            key={drug}
            onClick={() => {
              setQuery(drug);
              fetchSuggestions(drug);
            }}
            style={{
              padding: '4px 10px',
              borderRadius: '9999px',
              backgroundColor: '#f3e8ff',
              color: '#7c3aed',
              fontSize: '12px',
              fontWeight: '500',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            {drug}
          </button>
        ))}
      </div>
    </div>
  );

  // Render error state
  const renderError = () => (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <div style={{
        width: '48px',
        height: '48px',
        margin: '0 auto 12px',
        borderRadius: '50%',
        backgroundColor: '#fef2f2',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>
      </div>
      <p style={{ fontWeight: '500', color: '#1e293b', marginBottom: '4px', fontSize: '15px' }}>
        Something went wrong
      </p>
      <p style={{ color: '#64748b', fontSize: '13px' }}>
        {error}
      </p>
    </div>
  );

  // Render loading state in dropdown
  const renderLoading = () => (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <div style={{
        width: '48px',
        height: '48px',
        margin: '0 auto 12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#a855f7"
          style={{ animation: 'spin 1s linear infinite' }}
        >
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" strokeDasharray="60" strokeDashoffset="20" opacity="0.25"></circle>
          <path d="M12 2 A10 10 0 0 1 22 12" stroke="currentColor" strokeWidth="4" fill="none" strokeLinecap="round"></path>
        </svg>
      </div>
      <p style={{ color: '#64748b', fontSize: '13px' }}>
        Searching medications...
      </p>
    </div>
  );

  // Render dropdown content
  const renderDropdown = () => {
    if (!isOpen) return null;

    // Determine what to show in dropdown
    let content;
    if (isLoading) {
      content = renderLoading();
    } else if (error) {
      content = renderError();
    } else if (hasSearched && suggestions.length === 0) {
      content = renderNoResults();
    } else if (suggestions.length > 0) {
      content = suggestions.map((suggestion, index) => renderSuggestion(suggestion, index));
    } else {
      return null;
    }

    return ReactDOM.createPortal(
      <div
        ref={dropdownRef}
        style={{
          position: 'absolute',
          top: `${dropdownPosition.top + 4}px`,
          left: `${dropdownPosition.left}px`,
          width: `${dropdownPosition.width}px`,
          backgroundColor: '#ffffff',
          border: '1px solid rgba(124, 58, 237, 0.25)',
          borderRadius: '16px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
          maxHeight: '320px',
          overflowY: 'auto',
          zIndex: 99999
        }}
      >
        {content}
      </div>,
      document.body
    );
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      {/* Search Input */}
      <input
        ref={inputRef}
        type="text"
        name="q"
        value={query}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          updateDropdownPosition();
          if (suggestions.length > 0) {
            setIsOpen(true);
          }
        }}
        placeholder="Enter medication name (e.g., Lipitor, Metformin)"
        autoComplete="off"
        aria-label="Search for a medication by name"
        title="Type your prescription drug name to compare prices at pharmacies near you"
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-autocomplete="list"
        style={{
          width: '100%',
          height: '44px',
          padding: '10px 16px 10px 44px',
          fontSize: '14px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          fontWeight: '400',
          lineHeight: '1.5',
          color: '#1e293b',
          backgroundColor: '#ffffff',
          border: '1px solid #ddd6fe',
          borderRadius: '8px',
          outline: 'none',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          appearance: 'none'
        }}
        onFocus={(e) => {
          e.target.style.borderColor = '#a78bfa';
          e.target.style.boxShadow = '0 0 0 2px rgba(167, 139, 250, 0.15)';
        }}
        onBlur={(e) => {
          // Don't reset if clicking on dropdown
          setTimeout(() => {
            if (document.activeElement !== inputRef.current && !dropdownRef.current?.contains(document.activeElement)) {
              e.target.style.borderColor = '#ddd6fe';
              e.target.style.boxShadow = 'none';
            }
          }, 100);
        }}
      />

      {/* Loading Spinner */}
      {isLoading && (
        <div style={{
          position: 'absolute',
          right: '20px',
          top: '50%',
          transform: 'translateY(-50%)',
          pointerEvents: 'none'
        }}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            style={{
              animation: 'spin 1s linear infinite',
              color: '#a855f7'
            }}
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
              strokeDasharray="60"
              strokeDashoffset="20"
              opacity="0.25"
            />
            <path
              d="M12 2 A10 10 0 0 1 22 12"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
              strokeLinecap="round"
            />
          </svg>
        </div>
      )}

      {/* Render dropdown via portal */}
      {renderDropdown()}

      {/* Inline keyframe animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ModernDrugSearch;
